import * as XLSX from 'xlsx';
import type {
  WarehouseConsumptionReport,
  WarehouseConsumptionSplit,
} from '../services/inventoryReportService';
import { fromMinorUnits } from './money';

/**
 * CONSUMPTION-EXPORT.1 — Warehouse Consumption by Venue exports.
 *
 * XLSX: a By Venue sheet (one line per venue, with a total), a By Item sheet
 * (one line per venue and item, the shape a pivot table wants), and Metadata.
 * CSV: the By Item table under the same metadata block the register uses.
 *
 * Sold and Wasted are separate columns because "what did this venue SELL out of
 * the cellar" is the usual question and breakage folded into it overstates the
 * answer. They add up to Total.
 *
 * Cost columns appear only for a reader allowed to see stock costs; the API
 * leaves the fields out for anyone else, so there would be nothing to put in
 * them.
 */

const moneyMajor = (minor: number | null | undefined): number | '' =>
  minor == null ? '' : fromMinorUnits(minor);
const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};
const fmtDateOnly = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toISOString().slice(0, 10);
};
const sanitize = (raw: string): string => raw.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '');

type Cell = string | number;

const QTY_HEADERS = ['Sold Qty', 'Wasted Qty', 'Total Qty'];
const COST_HEADERS = ['Sold Cost', 'Wasted Cost', 'Total Cost'];

const amounts = (
  r: WarehouseConsumptionSplit & { quantity: number; cost?: number | null },
  showCost: boolean,
): Cell[] => [
  r.soldQuantity, r.wastedQuantity, r.quantity,
  ...(showCost ? [moneyMajor(r.soldCost), moneyMajor(r.wastedCost), moneyMajor(r.cost)] : []),
];

const venueLabel = (g: WarehouseConsumptionReport['byVenue'][number]): string =>
  g.venueName ?? (g.venueId ? `Venue ${g.venueId.slice(-6)}` : 'Company (unattributed)');

function venueTable(report: WarehouseConsumptionReport, showCost: boolean): Cell[][] {
  const headers = ['Venue', 'Administers warehouse', ...QTY_HEADERS, ...(showCost ? COST_HEADERS : [])];
  const rows = report.byVenue.map(g => [venueLabel(g), g.isHomeVenue ? 'Yes' : '', ...amounts(g, showCost)]);
  const total: Cell[] = ['TOTAL', '', ...amounts(report.totals, showCost)];
  return [headers, ...rows, total];
}

function itemTable(report: WarehouseConsumptionReport, showCost: boolean): Cell[][] {
  const headers = ['Venue', 'Item', 'Short ID', ...QTY_HEADERS, ...(showCost ? COST_HEADERS : [])];
  const rows = report.byVenue.flatMap(g => g.items.map(i => [
    venueLabel(g),
    i.itemName ?? `(deleted item ${i.inventoryItemId.slice(-6)})`,
    i.shortId ?? '',
    ...amounts(i, showCost),
  ]));
  return [headers, ...rows];
}

function metadataLines(report: WarehouseConsumptionReport): string[][] {
  const m = report.meta;
  return [
    ['Report', m.reportName],
    ['Warehouse', m.warehouseName ?? m.warehouseId],
    ['From', fmtDateOnly(m.from)],
    ['To', fmtDateOnly(m.to)],
    ['Movements included', String(m.movementCount)],
    ['Movements not attributable', String(m.excludedMovementCount)],
    ['Sold', 'SALE net of SALE_REVERSAL. Wasted is WASTE. They add up to Total.'],
    ['Generated at', fmtDateTime(m.generatedAt)],
    ...m.notes.map((n, i) => [i === 0 ? 'Notes' : '', n]),
  ];
}

function buildFilename(report: WarehouseConsumptionReport, ext: string): string {
  const wh = report.meta.warehouseName ? `-${sanitize(report.meta.warehouseName)}` : '';
  return `warehouse-consumption${wh}-${fmtDateOnly(report.meta.from)}-to-${fmtDateOnly(report.meta.to)}.${ext}`;
}

const widths = (aoa: Cell[][]) =>
  (aoa[0] ?? []).map((h, c) => ({
    wch: Math.min(48, Math.max(String(h).length + 2, ...aoa.map(r => String(r[c] ?? '').length + 1), 10)),
  }));

export function downloadWarehouseConsumptionXlsx(report: WarehouseConsumptionReport, showCost: boolean): void {
  const wb = XLSX.utils.book_new();

  const byVenue = venueTable(report, showCost);
  const venueWs = XLSX.utils.aoa_to_sheet(byVenue);
  venueWs['!cols'] = widths(byVenue);
  XLSX.utils.book_append_sheet(wb, venueWs, 'By Venue');

  const byItem = itemTable(report, showCost);
  const itemWs = XLSX.utils.aoa_to_sheet(byItem);
  itemWs['!cols'] = widths(byItem);
  XLSX.utils.book_append_sheet(wb, itemWs, 'By Item');

  const metaWs = XLSX.utils.aoa_to_sheet([['Field', 'Value'], ...metadataLines(report)]);
  metaWs['!cols'] = [{ wch: 26 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');

  XLSX.writeFile(wb, buildFilename(report, 'xlsx'));
}

function csvCell(v: Cell): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function downloadWarehouseConsumptionCsv(report: WarehouseConsumptionReport, showCost: boolean): void {
  const lines: string[] = [];
  for (const [k, v] of metadataLines(report)) lines.push(`${csvCell(k)},${csvCell(v)}`);
  lines.push('');
  for (const r of itemTable(report, showCost)) lines.push(r.map(csvCell).join(','));

  const blob = new Blob([lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildFilename(report, 'csv');
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/** Exposed for tests: the sheets exactly as the XLSX writes them. */
export const __tables = { venueTable, itemTable };
